import { useState, useEffect, useCallback } from 'react'
import {
  MdArrowBack,
  MdEdit,
  MdPerson,
  MdFlight,
  MdLocationOn,
  MdDescription,
  MdLoyalty,
  MdAccessibility,
  MdLanguage,
  MdRefresh,
  MdBusiness,
  MdAirplaneTicket,
} from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner, Badge, InfoCard } from '../../components/ui'
import { usePassenger } from '../../hooks/usePassenger'
import { useWebsocket } from '../../hooks/useWebsocket'
import {
  addSubscription,
  removeSubscription,
  selectPassengerItemUpdated,
  setPassengerItemUpdated,
} from '../../state/websocket.slice'
import { capitalizeStatus } from '../../utils/string'
import { getWebsocketChannel } from '../../utils/websocket'

function PassengerDetailPage() {
  const dispatch = useDispatch()
  const { passengerId } = useParams()
  const navigate = useNavigate()
  const [passenger, setPassenger] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()
  const passengerItemUpdated = useSelector(selectPassengerItemUpdated)

  const { getPassengerAsync } = usePassenger()

  const loadPassenger = useCallback(async () => {
    try {
      setIsLoading(true)
      const passengerData = await getPassengerAsync(passengerId)
      setPassenger(passengerData)
    } catch (error) {
      console.error('Error loading passenger:', error)
    } finally {
      setIsLoading(false)
    }
  }, [passengerId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (passengerId) {
      loadPassenger()
    }
  }, [passengerId, loadPassenger])

  useEffect(() => {
    const channelName = getWebsocketChannel(passengerId).passengerDetails

    if (channelName && passengerId) {
      dispatch(addSubscription(channelName))
      subscribeToChannel(channelName)
    }

    return () => {
      if (channelName && passengerId) {
        dispatch(removeSubscription(channelName))
        unsubscribeFromChannel(channelName)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (passengerItemUpdated) {
      setPassenger(passengerItemUpdated)
      dispatch(setPassengerItemUpdated(undefined))
    }
  }, [passengerItemUpdated]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getDaysUntilExpiry = (dateString) => {
    if (!dateString) return null
    const expiryDate = new Date(dateString)
    const today = new Date()
    const diffTime = expiryDate - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const getExpiryStatus = (dateString) => {
    const daysUntil = getDaysUntilExpiry(dateString)
    if (daysUntil === null) return 'unknown'
    if (daysUntil < 0) return 'expired'
    if (daysUntil <= 30) return 'warning'
    return 'good'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
      case 'inactive':
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    }
  }

  const getLoyaltyTierColor = (tier) => {
    switch (tier) {
      case 'platinum':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400'
      case 'gold':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400'
      case 'silver':
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
      case 'premium':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    }
  }

  const getExpiryColor = (status) => {
    switch (status) {
      case 'expired':
        return 'text-red-600 dark:text-red-400'
      case 'warning':
        return 'text-warning-600 dark:text-warning-400'
      case 'good':
        return 'text-success-600 dark:text-success-400'
      default:
        return 'text-neutral-500 dark:text-neutral-400'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!passenger) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
            Passenger not found
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The passenger you're looking for doesn't exist or has been removed.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2"
          >
            <MdArrowBack className="text-lg" />
            <span>Back to Passengers</span>
          </Button>
        </div>
      </div>
    )
  }

  const passportExpiryStatus = getExpiryStatus(passenger.passport?.expiryDate)

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="text-left">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 mb-4"
          >
            <MdArrowBack />
            <span>Back to Passengers</span>
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-neutral-900 dark:text-white mb-2 text-left">
              {passenger.firstName} {passenger.lastName}
            </h1>
            <p className="text-base lg:text-lg text-neutral-600 dark:text-neutral-400 text-left">
              {passenger.email} • {passenger.phone}
            </p>
          </div>

          <div className="flex items-center space-x-3 flex-shrink-0">
            <Button variant="ghost" onClick={loadPassenger} disabled={isLoading}>
              <MdRefresh className={isLoading ? 'animate-spin' : ''} />
            </Button>

            <div className="flex items-center space-x-2">
              <span
                className={`px-3 lg:px-4 py-2 text-sm font-medium rounded-full ${getStatusColor(passenger.status)}`}
              >
                {capitalizeStatus(passenger.status)}
              </span>
              {passenger.loyaltyProgram?.tier && (
                <span
                  className={`px-3 lg:px-4 py-2 text-sm font-medium rounded-full ${getLoyaltyTierColor(passenger.loyaltyProgram.tier)}`}
                >
                  {passenger.loyaltyProgram.tier.charAt(0).toUpperCase() +
                    passenger.loyaltyProgram.tier.slice(1)}
                </span>
              )}
            </div>

            <Button
              variant="primary"
              onClick={() => {
                /* TODO: Implement edit passenger */
              }}
              className="flex items-center space-x-2"
            >
              <MdEdit className="text-lg" />
              <span>Edit</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <InfoCard title="Personal Information" icon={MdPerson}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Full Name</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passenger.firstName} {passenger.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Date of Birth</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {formatDate(passenger.dateOfBirth)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Gender</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passenger.gender || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Nationality</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passenger.nationality || 'N/A'}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Email</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passenger.email}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Phone</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passenger.phone || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Status</span>
                  <Badge variant="success">{capitalizeStatus(passenger.status)}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Member Since</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {formatDate(passenger.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          </InfoCard>

          {/* Passport Information */}
          {passenger.passport && (
            <InfoCard title="Passport Information" icon={MdDescription}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Passport Number</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {passenger.passport.number}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Country</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {passenger.passport.country}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Issue Date</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {formatDate(passenger.passport.issueDate)}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Expiry Date</span>
                    <span className={`font-medium ${getExpiryColor(passportExpiryStatus)}`}>
                      {formatDate(passenger.passport.expiryDate)}
                      {passportExpiryStatus === 'expired' && ' (Expired)'}
                      {passportExpiryStatus === 'warning' &&
                        ` (${getDaysUntilExpiry(passenger.passport.expiryDate)} days)`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Status</span>
                    <Badge
                      variant={
                        passportExpiryStatus === 'expired'
                          ? 'error'
                          : passportExpiryStatus === 'warning'
                            ? 'warning'
                            : 'success'
                      }
                    >
                      {passportExpiryStatus === 'expired'
                        ? 'Expired'
                        : passportExpiryStatus === 'warning'
                          ? 'Expiring Soon'
                          : 'Valid'}
                    </Badge>
                  </div>
                </div>
              </div>
            </InfoCard>
          )}

          {/* Contact Information */}
          {passenger.contactInfo && (
            <InfoCard title="Contact Information" icon={MdLocationOn}>
              <div className="space-y-4">
                {/* Address */}
                {passenger.contactInfo.address && (
                  <div>
                    <h4 className="font-medium text-neutral-900 dark:text-white mb-2">Address</h4>
                    <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                      <p className="text-neutral-900 dark:text-white">
                        {passenger.contactInfo.address.street}
                      </p>
                      <p className="text-neutral-900 dark:text-white">
                        {passenger.contactInfo.address.city}, {passenger.contactInfo.address.state}{' '}
                        {passenger.contactInfo.address.zipCode}
                      </p>
                      <p className="text-neutral-900 dark:text-white">
                        {passenger.contactInfo.address.country}
                      </p>
                    </div>
                  </div>
                )}

                {/* Emergency Contact */}
                {passenger.contactInfo.emergencyContact && (
                  <div>
                    <h4 className="font-medium text-neutral-900 dark:text-white mb-2">
                      Emergency Contact
                    </h4>
                    <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                      <div className="flex justify-between">
                        <span className="text-neutral-600 dark:text-neutral-400">Name</span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {passenger.contactInfo.emergencyContact.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600 dark:text-neutral-400">Relationship</span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {passenger.contactInfo.emergencyContact.relationship}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600 dark:text-neutral-400">Phone</span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {passenger.contactInfo.emergencyContact.phone}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </InfoCard>
          )}

          {/* Loyalty Program */}
          {passenger.loyaltyProgram && (
            <InfoCard title="Loyalty Program" icon={MdLoyalty}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Points</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {passenger.loyaltyProgram.points?.toLocaleString() || '0'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Tier</span>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getLoyaltyTierColor(passenger.loyaltyProgram.tier)}`}
                    >
                      {passenger.loyaltyProgram.tier?.charAt(0).toUpperCase() +
                        passenger.loyaltyProgram.tier?.slice(1) || 'Standard'}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Member Since</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {formatDate(passenger.createdAt)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Last Updated</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {formatDate(passenger.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </InfoCard>
          )}

          {/* Preferences */}
          {passenger.preferences && (
            <InfoCard title="Travel Preferences" icon={MdFlight}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Seat Preference</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {passenger.preferences.seatPreference || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Meal Preference</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {passenger.preferences.mealPreference || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">
                      Preferred Language
                    </span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {passenger.preferences.preferredLanguage || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">
                      Special Assistance
                    </span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {passenger.preferences.specialAssistance?.length > 0
                        ? passenger.preferences.specialAssistance.join(', ')
                        : 'None'}
                    </span>
                  </div>
                </div>
              </div>
            </InfoCard>
          )}

          {/* Special Requirements */}
          {passenger.specialRequirements && (
            <InfoCard title="Special Requirements" icon={MdAccessibility}>
              <div className="space-y-4">
                {/* Mobility Assistance */}
                {passenger.specialRequirements.mobilityAssistance && (
                  <div>
                    <h4 className="font-medium text-neutral-900 dark:text-white mb-2">
                      Mobility Assistance
                    </h4>
                    <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                      <div className="flex justify-between">
                        <span className="text-neutral-600 dark:text-neutral-400">Required</span>
                        <Badge
                          variant={
                            passenger.specialRequirements.mobilityAssistance.required
                              ? 'warning'
                              : 'success'
                          }
                        >
                          {passenger.specialRequirements.mobilityAssistance.required ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}

                {/* Medical Conditions */}
                {passenger.specialRequirements.medicalConditions &&
                  passenger.specialRequirements.medicalConditions.length > 0 && (
                    <div>
                      <h4 className="font-medium text-neutral-900 dark:text-white mb-2">
                        Medical Conditions
                      </h4>
                      <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                        <div className="flex flex-wrap gap-2">
                          {passenger.specialRequirements.medicalConditions.map(
                            (condition, index) => (
                              <Badge key={index} variant="error">
                                {condition}
                              </Badge>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Dietary Restrictions */}
                {passenger.specialRequirements.dietaryRestrictions &&
                  passenger.specialRequirements.dietaryRestrictions.length > 0 && (
                    <div>
                      <h4 className="font-medium text-neutral-900 dark:text-white mb-2">
                        Dietary Restrictions
                      </h4>
                      <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                        <div className="flex flex-wrap gap-2">
                          {passenger.specialRequirements.dietaryRestrictions.map(
                            (restriction, index) => (
                              <Badge key={index} variant="warning">
                                {restriction}
                              </Badge>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </InfoCard>
          )}

          {/* Communication Preferences */}
          {passenger.communicationPreferences && (
            <InfoCard title="Communication Preferences" icon={MdLanguage}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">Marketing Emails</span>
                    <Badge
                      variant={
                        passenger.communicationPreferences.marketingEmails ? 'success' : 'neutral'
                      }
                    >
                      {passenger.communicationPreferences.marketingEmails ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">
                      SMS Notifications
                    </span>
                    <Badge
                      variant={
                        passenger.communicationPreferences.smsNotifications ? 'success' : 'neutral'
                      }
                    >
                      {passenger.communicationPreferences.smsNotifications ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">
                      Push Notifications
                    </span>
                    <Badge
                      variant={
                        passenger.communicationPreferences.pushNotifications ? 'success' : 'neutral'
                      }
                    >
                      {passenger.communicationPreferences.pushNotifications
                        ? 'Enabled'
                        : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-400">
                      Preferred Currency
                    </span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {passenger.communicationPreferences.preferredCurrency || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </InfoCard>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <InfoCard title="Quick Stats" icon={MdFlight}>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Total Bookings</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {passenger.bookingHistory?.length || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Payment Methods</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {passenger.paymentMethods?.length || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Travel Documents</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {passenger.travelDocuments?.length || 0}
                </span>
              </div>
            </div>
          </InfoCard>

          {/* Associated Airlines */}
          {passenger.airline && (
            <InfoCard title="Associated Airline" icon={MdBusiness}>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Airline ID</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passenger.airline}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/airlines/${passenger.airline}`)}
                  className="w-full flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  <MdBusiness className="text-sm" />
                  View Airline Details
                </Button>
              </div>
            </InfoCard>
          )}

          {/* Associated Aircraft */}
          {passenger.aircraft && (
            <InfoCard title="Associated Aircraft" icon={MdAirplaneTicket}>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Aircraft ID</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passenger.aircraft}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/aircraft/${passenger.aircraft}`)}
                  className="w-full flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  <MdAirplaneTicket className="text-sm" />
                  View Aircraft Details
                </Button>
              </div>
            </InfoCard>
          )}

          {/* System Information */}
          <InfoCard title="System Information" icon={MdDescription}>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Created</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {formatDateTime(passenger.createdAt)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Last Updated</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {formatDateTime(passenger.updatedAt)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">ID</span>
                <span className="font-mono text-xs text-neutral-500 dark:text-neutral-400">
                  {passenger._id}
                </span>
              </div>
            </div>
          </InfoCard>
        </div>
      </div>
    </div>
  )
}

export default PassengerDetailPage
